import { useState, useCallback } from 'react';
import { getCSRFToken } from '../utils/csrf';
import { route } from '../utils/route';
import type { ShoppingListResponse } from '../types/smartList';

interface UseShoppingListReturn {
  submitList: (shoppingList: string) => Promise<void>;
  isLoading: boolean;
  result: ShoppingListResponse | null;
  error: string | null;
  reset: () => void;
  removeCartItem: (index: number) => void;
}

export function useShoppingList(): UseShoppingListReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ShoppingListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submitList = useCallback(async (shoppingList: string) => {
    setIsLoading(true);
    setResult(null);
    setError(null);

    try {
      const response = await fetch(route('api.cart.parse_shopping_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
        },
        credentials: 'include',
        body: JSON.stringify({ shopping_list: shoppingList }),
      });

      const data: ShoppingListResponse = await response.json();

      if (response.ok && data.success) {
        setResult(data);
      } else {
        setError(data.message || 'Ocurrió un error al procesar tu lista');
      }
    } catch (err) {
      setError('Error de conexión: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const removeCartItem = useCallback((index: number) => {
    setResult(prev => {
      if (!prev) return prev;
      const newCart = prev.cart.filter((_, i) => i !== index);
      return {
        ...prev,
        cart: newCart,
        items_added_to_cart: newCart.length,
      };
    });
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return { submitList, isLoading, result, error, reset, removeCartItem };
}
